import Skeleton from '@mui/material/Skeleton';
import classNames from 'classnames';
import { bool, func, object, oneOfType, string } from 'prop-types';
import React, { useState } from 'react';
import { InlineAlert } from '../../../containers/EditListingPage/EditListingWizard/components/InlineAlert';
import { FormattedMessage } from '../../../util/reactIntl';
import { toSearchParams } from '../../../util/routes';
import { propTypes } from '../../../util/types';
import TrackingButton from '../../common/TrackingButton';
import { NamedLink } from '../../index';
import { PrimaryButton } from '../../mui/Button';
import { SWLink } from '../../mui/Link';
import css from './SubmitBookingPanel.module.css';
import { TRACKING_EVENTS } from '../../../analytics/trackingEvent';
import { BookedNote } from './BookedNote';
import { isIndividualSession } from '../../../util/soulwise-marketplace/listingType';

const NoBookingNote = () => (
  <InlineAlert>
    <FormattedMessage id="SubmitBookingPanel.schoolHandlesBookings" />
  </InlineAlert>
);

export const ErrorNote = ({ message }) => (
  <div className={css.errorNote}>
    <div className={css.errorNote_body}>{message}</div>
  </div>
);

const SubmitButton = ({ bookingInfo, buttonProps, isIndividual }) => {
  const trackingData = {
    listing_id: bookingInfo?.listingId,
  };

  const buttonLabel =
    bookingInfo?.isPaidListing && isIndividual
      ? 'SubmitButton.individualPaidListing'
      : bookingInfo?.isPaidListing
      ? 'SubmitButton.isPaidListing'
      : bookingInfo?.isFreeListing
      ? 'SubmitButton.isFreeListing'
      : bookingInfo?.isPayLaterListing
      ? 'SubmitButton.isPayLaterListing'
      : 'BookingTimeForm.requestToBook';

  return (
    <TrackingButton
      eventName={TRACKING_EVENTS.BOOKING_SUBMIT_BUTTON_CLICKED}
      trackingData={trackingData}
      sx={{ width: '100%' }}
      {...buttonProps}
    >
      <FormattedMessage id={buttonLabel} />
    </TrackingButton>
  );
};

export const SubmitBookingPanel = props => {
  const {
    rootClassName,
    fetchUserTransactionsInProgress,
    bookingInfo,
    buttonProps,
    listing,
    cancelCurrentBookingInProgress,
    cancelCurrentBookingError,
    onCancelCurrentBooking,
    instantOrderError,
    hasTimeslots,
    requiresTimeslots,
    stickyBar,
  } = props;

  const [clickCount, setClickCount] = useState(0);
  const [isBlocked, setIsBlocked] = useState(false);

  const {
    isCurrentlyBooked,
    isBookingClosed,
    isBookingViaSoulwiseNotAvailable,
    isPayLaterListing,
    isFreeListing,
    isOwnListing,
  } = bookingInfo;

  const listingType = listing?.attributes?.publicData?.listingType;
  const isIndividual = isIndividualSession(listingType);
  const rootClasses = classNames(stickyBar ? css.stickyRoot : rootClassName || css.root);
  const inProgress = fetchUserTransactionsInProgress || cancelCurrentBookingInProgress;
  const hasError = cancelCurrentBookingError || instantOrderError || isBlocked;

  const buttonPropsMaybe = {
    ...buttonProps,
    onClick: buttonProps?.onClick,
    type: buttonProps?.type,
  };

  if (requiresTimeslots && !hasTimeslots && !inProgress) {
    return (
      <PrimaryButton disabled={true} sx={{ width: '100%' }}>
        <FormattedMessage id="OrderPanel.noTimeslotsButtonText" />
      </PrimaryButton>
    );
  }

  if (isBookingClosed) {
    return (
      <PrimaryButton disabled={true} sx={{ width: '100%' }}>
        <FormattedMessage id="OrderPanel.closedListingButtonText" />
      </PrimaryButton>
    );
  }

  if (isBookingViaSoulwiseNotAvailable) {
    return <NoBookingNote />;
  }

  return (
    <div className={rootClasses}>
      {inProgress && !hasError ? <Skeleton variant="rectangular" width="100%" height={43} /> : null}

      {cancelCurrentBookingError ? (
        <ErrorNote
          message={'ERROR: Cannot cancel booking. Please refresh page or contact support'}
        />
      ) : null}
      {instantOrderError ? (
        <ErrorNote
          message={`ERROR (${instantOrderError?.status}): Please refresh page or contact support`}
        />
      ) : null}
      {isBlocked ? (
        <ErrorNote message={'ERROR: Too many clicks. Please slow down and refresh the page.'} />
      ) : null}

      {!inProgress && !hasError ? (
        <>
          {isCurrentlyBooked && !stickyBar ? (
            <BookedNote
              bookingInfo={bookingInfo}
              onCancelCurrentBooking={onCancelCurrentBooking}
              listing={listing}
            />
          ) : null}
          <SubmitButton
            buttonProps={buttonPropsMaybe}
            bookingInfo={bookingInfo}
            isIndividual={isIndividual}
          />
          {isPayLaterListing || isFreeListing || isOwnListing ? null : (
            <p className={css.finePrint}>
              <FormattedMessage id={'BookingTimeForm.youWontBeChargedInfo'} />
            </p>
          )}
        </>
      ) : null}
    </div>
  );
};

// We are covering only booking but for reference I'm adding what can also be covered
// {isBooking ? (
//   bookingInfo?.isPayLaterListing ? (
//     <FormattedMessage id="BookingTimeForm.imInterestedBooking" />
//   ) : (
//     <FormattedMessage id="OrderPanel.ctaButtonMessageBooking" />
//   )
// ) : isOutOfStock ? (
//   <FormattedMessage id="OrderPanel.ctaButtonMessageNoStock" />
// ) : isPurchase ? (
//   <FormattedMessage id="OrderPanel.ctaButtonMessagePurchase" />
// ) : (
//   <FormattedMessage id="OrderPanel.ctaButtonMessageInquiry" />
// )}

SubmitBookingPanel.defaultProps = {
  bookingInfo: {},
  requiresTimeslots: true,
  stickyBar: false,
};

SubmitBookingPanel.propTypes = {
  buttonProps: object,
  rootClassName: string,
  cancelCurrentBookingError: propTypes.error,
  bookingInfo: propTypes.bookingInfo,
  cancelCurrentBookingInProgress: bool,
  fetchUserTransactionsInProgress: bool,
  onCancelCurrentBooking: func,
  listing: oneOfType([propTypes.listing, propTypes.ownListing]),
  hasTimeslots: bool,
  requiresTimeslots: bool,
  stickyBar: bool,
};
